import { useEffect, useRef, useState } from "react";

const WOLF = "https://static.prod-images.emergentagent.com/jobs/40c7c30a-9eb9-4989-9e5f-8b4b7790c9c2/images/9d9e195b14878004e29046debeb5d970c5c4fce5a73b605af2bff26d7871e390.png";
const SHAMAN = "https://static.prod-images.emergentagent.com/jobs/40c7c30a-9eb9-4989-9e5f-8b4b7790c9c2/images/221a49764da8d905b31984e9b96e55716520ce740c93b16d791cc48962c175c3.png";

// Constelatia Lira (coordonate locale, unitate 0..1)
const LYRA = {
  vega: [0.5, 0.0],
  epsilon: [0.7, 0.14],
  zeta: [0.3, 0.2],
  delta: [0.66, 0.42],
  beta: [0.28, 0.56],
  gamma: [0.62, 0.68],
};
const LYRA_EDGES = [
  ["vega", "epsilon"],
  ["vega", "zeta"],
  ["zeta", "delta"],
  ["epsilon", "delta"],
  ["zeta", "beta"],
  ["delta", "gamma"],
  ["beta", "gamma"],
];

export const Starfield = () => {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState(0); // 0 = lup, 1 = saman

  useEffect(() => {
    const onScroll = () => {
      const max = document.body.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? window.scrollY / max : 0;
      setPhase(ratio > 0.45 ? 1 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let stars = [];
    let w, h;
    let t = 0;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      const count = Math.min(360, Math.floor((w * h) / 6000));
      stars = Array.from({ length: count }, () => {
        const big = Math.random() > 0.9;
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          r: big ? Math.random() * 1.4 + 1.1 : Math.random() * 1.1 + 0.3,
          a: Math.random() * Math.PI * 2,
          sp: Math.random() * 0.02 + 0.004,
          drift: Math.random() * 0.08 + 0.015,
          gold: Math.random() > 0.82,
        };
      });
    };

    const drawConstellation = () => {
      const ax = w * 0.8;
      const ay = h * 0.2;
      const sc = Math.min(w, h) * (w < 640 ? 0.26 : 0.17);
      const px = (k) => ax + (LYRA[k][0] - 0.5) * sc * 2;
      const py = (k) => ay + LYRA[k][1] * sc * 2;

      // linii
      ctx.lineWidth = 0.8;
      ctx.strokeStyle = "rgba(214, 181, 109, 0.32)";
      ctx.beginPath();
      for (const [a, b] of LYRA_EDGES) {
        ctx.moveTo(px(a), py(a));
        ctx.lineTo(px(b), py(b));
      }
      ctx.stroke();

      // stele constelatie
      Object.keys(LYRA).forEach((k, i) => {
        const isVega = k === "vega";
        const tw = 0.6 + Math.abs(Math.sin(t * 0.04 + i)) * 0.4;
        const r = isVega ? 3.2 : 1.8;
        const x = px(k);
        const y = py(k);
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r * 5);
        grad.addColorStop(0, `rgba(255, 240, 200, ${tw})`);
        grad.addColorStop(0.3, `rgba(214, 181, 109, ${tw * 0.5})`);
        grad.addColorStop(1, "rgba(214, 181, 109, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, r * 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(255, 248, 230, ${tw})`;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      });

      // eticheta
      ctx.fillStyle = "rgba(214, 181, 109, 0.6)";
      ctx.font = "600 11px Outfit, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("L Y R A", ax, py("gamma") + 28);
    };

    const draw = () => {
      t += 1;
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        s.a += s.sp;
        const alpha = 0.35 + Math.abs(Math.sin(s.a)) * 0.65;
        s.y += s.drift;
        if (s.y > h) s.y = 0;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.gold
          ? `rgba(224, 196, 130, ${alpha})`
          : `rgba(226, 236, 255, ${alpha})`;
        ctx.fill();
      }
      drawConstellation();
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  const totemCls =
    "absolute top-[40%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(94vw,640px)] md:w-[min(90vh,880px)] aspect-square object-contain transition-opacity duration-[1400ms] ease-in-out";

  return (
    <div
      data-testid="mystic-background"
      className="fixed inset-0 z-0 overflow-hidden pointer-events-none"
      style={{ background: "#04050A" }}
    >
      {/* nebuloase albastre */}
      <div
        className="nebula animate-[nebula-drift-1_26s_ease-in-out_infinite]"
        style={{
          width: "58vw",
          height: "58vw",
          top: "-12%",
          left: "-6%",
          background:
            "radial-gradient(circle, rgba(28,62,130,0.34), transparent 64%)",
        }}
      />
      <div
        className="nebula animate-[nebula-drift-2_32s_ease-in-out_infinite]"
        style={{
          width: "52vw",
          height: "52vw",
          bottom: "-16%",
          right: "-8%",
          background:
            "radial-gradient(circle, rgba(214,181,109,0.13), transparent 65%)",
        }}
      />
      <div
        className="nebula animate-[nebula-drift-3_38s_ease-in-out_infinite]"
        style={{
          width: "46vw",
          height: "46vw",
          top: "34%",
          left: "44%",
          background:
            "radial-gradient(circle, rgba(16,38,86,0.42), transparent 60%)",
        }}
      />

      {/* totemuri: lup (prima jumatate) / saman (a doua jumatate) — blend pe acelasi element */}
      <img
        src={WOLF}
        alt=""
        data-testid="totem-wolf"
        className={totemCls}
        style={{ opacity: phase === 0 ? 0.6 : 0, mixBlendMode: "screen" }}
      />
      <img
        src={SHAMAN}
        alt=""
        data-testid="totem-shaman"
        className={totemCls}
        style={{ opacity: phase === 1 ? 0.6 : 0, mixBlendMode: "screen" }}
      />

      {/* cer instelat + constelatia Lira */}
      <canvas ref={canvasRef} className="absolute inset-0" />

      {/* glow sus + vigneta */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(40,80,160,0.08), transparent 45%)",
        }}
      />
      <div className="absolute inset-0 vignette" />
    </div>
  );
};
